import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';
import { cx } from './cx.ts';

type Variant = 'primary' | 'secondary' | 'soft' | 'ghost' | 'danger' | 'in' | 'out';
type Size = 'lg' | 'md' | 'sm';

const VARIANT: Record<Variant, string> = {
  primary: 'bg-brand text-brand-ink hover:bg-brand-hover shadow-[0_1px_0_rgb(0_0_0/0.08)]',
  secondary: 'bg-surface text-ink border border-line hover:bg-surface-2 hover:border-line-strong',
  soft: 'bg-surface-2 text-ink hover:bg-surface-3',
  ghost: 'text-ink-2 hover:bg-surface-2 hover:text-ink',
  danger: 'bg-surface text-out border border-line hover:bg-out-soft',
  in: 'bg-in text-white hover:opacity-95',
  out: 'bg-out text-white hover:opacity-95',
};

const SIZE: Record<Size, string> = {
  lg: 'h-13 px-5 text-[16px] rounded-2xl gap-2.5',
  md: 'h-11 px-4 text-[15px] rounded-xl gap-2',
  sm: 'h-9 px-3 text-[14px] rounded-[10px] gap-1.5',
};

const base =
  'press inline-flex select-none items-center justify-center font-semibold tracking-[-0.005em] whitespace-nowrap ' +
  'disabled:pointer-events-none disabled:opacity-45';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
  block?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading, icon, block, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(base, VARIANT[variant], SIZE[size], block && 'w-full', className)}
      {...rest}
    >
      {loading ? <Spinner /> : icon}
      {children}
    </button>
  );
});

export function ButtonLink({ variant = 'primary', size = 'md', icon, block, className, children, ...rest }:
  LinkProps & { variant?: Variant; size?: Size; icon?: ReactNode; block?: boolean }) {
  return (
    <Link className={cx(base, VARIANT[variant], SIZE[size], block && 'w-full', className)} {...rest}>
      {icon}
      {children}
    </Link>
  );
}

export const IconButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { label: string; tone?: 'plain' | 'soft' }>(
  function IconButton({ label, tone = 'plain', className, children, type = 'button', ...rest }, ref) {
    return (
      <button
        ref={ref}
        type={type}
        aria-label={label}
        title={label}
        className={cx(
          'press hit inline-flex size-10 items-center justify-center rounded-full text-ink-2 hover:text-ink',
          tone === 'soft' ? 'bg-surface-2 hover:bg-surface-3' : 'hover:bg-surface-2',
          'disabled:opacity-40',
          className,
        )}
        {...rest}
      >
        {children}
      </button>
    );
  },
);

export function Spinner({ className }: { className?: string }) {
  return (
    <svg className={cx('size-[18px] animate-spin', className)} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}
